import { useEffect, useRef, useState } from 'react';

const faqs = [
  {
    q: '加入 LINE@ 之後，一定要報名嗎？',
    a: '完全不需要。加入 LINE@ 是為了讓我們有機會聊聊你的狀況，了解古箏學習是否適合你。這裡不會有任何「一定要報名」的壓力，聊完之後你可以自由決定下一步，甚至停在那裡也沒關係。',
  },
  {
    q: '沒有音樂基礎可以學古箏嗎？',
    a: '可以，而且完全沒有基礎往往是最好的起點。古箏是五聲音階樂器，不需要任何樂理知識就能開始感受音樂。學過其他樂器的人有時反而需要先放掉既有的習慣，空白的狀態讓你更容易進入古箏自己的語言。',
  },
  {
    q: '成人幾歲都可以開始學嗎？',
    a: '是的。我的學生裡有大學生、上班族、三十幾歲的媽媽，也有退休的長輩。古箏不設「來不及的年紀」，你只需要一個真正想開始的心。成人的專注力和對感受的細膩程度，其實是很大的學習優勢。',
  },
  {
    q: '開始學之前需要先買古箏嗎？',
    a: '不需要。建議先上幾堂課，確認自己和古箏之間有感覺之後，再討論是否購買。衝動買了樂器然後發現不適合，是不必要的負擔。如果有需要，我也可以幫你找到合適的租借管道。',
  },
  {
    q: '一週要練幾次？每次要練多久？',
    a: '初學階段，每週練習三到四次、每次二十到三十分鐘已經足夠。最重要的不是時間長度，而是規律性。如果你某段時間特別忙，我們可以彈性調整課程節奏，不會因此有壓力。',
  },
  {
    q: '課程費用大概是多少？',
    a: '課程費用依課程類型和時數而有所不同，歡迎加入 LINE@ 詢問目前的方案。在確認費用之前，我更希望先確認這裡是否適合你，再來談費用會更有意義。',
  },
  {
    q: '孩子幾歲可以開始學古箏？',
    a: '一般來說，五到六歲是比較合適的起點，但年齡只是參考，更重要的是孩子是否對聲音有好奇心，以及家長能否提供穩定的陪伴支持。如果你有特定的孩子狀況想討論，歡迎直接聊聊。',
  },
  {
    q: '體驗課之後，可以不報名嗎？',
    a: '當然可以。體驗課的目的是讓你感受這個學習環境和教學方式是否適合你，而不是為了說服你報名。如果體驗後覺得不合適，我很樂意幫你分析原因，甚至給你其他方向的建議。',
  },
];

export default function ContactFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <section id="faq" className="py-20 md:py-28 bg-[#faf8f5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-6 h-px bg-[#b08060]"></div>
          <span className="text-[0.8125rem] tracking-[0.2em] text-[#b08060] font-medium uppercase">FAQ</span>
        </div>
        <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-16 mb-14">
          <h2
            className={`font-serif-tc text-[#2c2420] leading-[1.35] transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
          >
            你可能有的疑問
          </h2>
          <p
            className={`text-[1rem] text-[#7a6e62] md:mb-1 transition-all duration-700 delay-100 ${
              visible ? 'opacity-100' : 'opacity-0'
            }`}
          >
            找到你想問的那一個，或直接加入 LINE@ 告訴我。
          </p>
        </div>

        <div
          ref={ref}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-3 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="border border-[#e0d4c0] rounded-xl overflow-hidden bg-white"
            >
              <button
                onClick={() => toggle(i)}
                className="w-full flex items-start justify-between gap-4 px-5 py-4 text-left cursor-pointer group"
              >
                <span className="font-serif-tc text-[#2c2420] text-[0.9375rem] leading-[1.5] group-hover:text-[#7a6248] transition-colors flex-1">
                  {faq.q}
                </span>
                <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                  <i
                    className={`ri-add-line text-[#b08060] text-lg transition-transform duration-300 ${
                      openIndex === i ? 'rotate-45' : ''
                    }`}
                  ></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-400 ${
                  openIndex === i ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-5 pb-5">
                  <div className="h-px bg-[#ede5d8] mb-4"></div>
                  <p className="text-[0.9375rem] leading-[1.75] text-[#5a4e42]">{faq.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-12 text-center transition-all duration-700 delay-300 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          <p className="text-[1rem] text-[#7a6e62] mb-4">
            還有其他問題？直接問我，沒有笨問題。
          </p>
          <a
            href="https://line.me"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 text-[#7a6248] text-[0.9375rem] font-medium underline underline-offset-4 hover:text-[#5a3e22] transition-colors cursor-pointer"
          >
            <i className="ri-line-fill text-lg"></i>
            加入 LINE@ 諮詢
          </a>
        </div>
      </div>
    </section>
  );
}
